import { useState, useEffect } from "react";
import { ChevronRight } from "lucide-react";
import { DEFAULT_PRODUCTS } from "@/lib/content";
import type { Product } from "@/types/content";
import { motion, AnimatePresence } from "framer-motion";

const getDesignConfig = (index: number) => {
  switch (index % 4) {
    case 0: // Studio Grey (Matches Naber Studios Image)
      return {
        bgStyle: "radial-gradient(circle at 50% 30%, rgba(85, 93, 107, 0.4) 0%, transparent 50%), linear-gradient(to bottom, #2c3038 0%, #1a1c21 75%, #a6acb5 78%, #6a717d 100%)",
        hideOrbs: true,
        hideText: true,
        gradientStart: "#2e3239",
        gradientEnd: "#1f2227",
        orb1: "bg-transparent",
        orb2: "bg-transparent",
        textPrimary: "text-[#f36453]", 
        textSecondary: "text-gray-400",
        textAccent: "text-[#f36453]/60",
        bgText: "text-white/0",
        btnBg: "bg-[#f36453] text-white hover:bg-[#e05849] shadow-lg",
        thumbBg: "bg-white/5 border-white/10",
        shadow: "bg-black/90",
        sizeBtnActive: "bg-[#f36453] text-white shadow-lg",
        sizeBtnInactive: "bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10"
      };
    case 1: // Light Silver
      return {
        gradientStart: "#E8E9EB",
        gradientEnd: "#C4C5C8",
        orb1: "bg-white/40",
        orb2: "bg-blue-200/40",
        textPrimary: "text-gray-900",
        textSecondary: "text-gray-600",
        textAccent: "text-gray-500",
        bgText: "text-black/5",
        btnBg: "bg-black/10 backdrop-blur-md border border-black/20 text-black hover:bg-black/20 shadow-[0_4px_30px_rgba(0,0,0,0.1)]",
        thumbBg: "bg-black/5 border-black/10",
        shadow: "bg-black/20",
        sizeBtnActive: "bg-gray-900 text-white shadow-[0_0_20px_rgba(0,0,0,0.15)]",
        sizeBtnInactive: "bg-black/5 text-gray-700 hover:bg-black/10 border border-black/10"
      };
    case 2: // Warm Grey
      return {
        gradientStart: "#D5D5D5",
        gradientEnd: "#BDBDBD",
        orb1: "bg-orange-500/10",
        orb2: "bg-yellow-500/10",
        textPrimary: "text-gray-900",
        textSecondary: "text-gray-700",
        textAccent: "text-gray-500",
        bgText: "text-black/10",
        btnBg: "bg-black/10 backdrop-blur-md border border-black/20 text-black hover:bg-black/20 shadow-[0_4px_30px_rgba(0,0,0,0.1)]",
        thumbBg: "bg-black/5 border-black/10",
        shadow: "bg-black/20",
        sizeBtnActive: "bg-gray-900 text-white shadow-[0_0_20px_rgba(0,0,0,0.15)]",
        sizeBtnInactive: "bg-black/5 text-gray-700 hover:bg-black/10 border border-black/10"
      };
    case 3: // Dark Space Grey
      return {
        gradientStart: "#15181E",
        gradientEnd: "#0A0C10",
        orb1: "bg-blue-500/10",
        orb2: "bg-indigo-500/10",
        textPrimary: "text-white",
        textSecondary: "text-gray-400",
        textAccent: "text-gray-500",
        bgText: "text-white/5",
        btnBg: "bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 shadow-[0_4px_30px_rgba(0,0,0,0.1)]",
        thumbBg: "bg-white/5 border-white/10",
        shadow: "bg-black/80",
        sizeBtnActive: "bg-white text-black shadow-[0_0_20px_rgba(255,255,255,0.3)]",
        sizeBtnInactive: "bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10"
      };
    default:
      return {
        gradientStart: "#1A1C23",
        gradientEnd: "#0D1017",
        orb1: "bg-blue-500/10",
        orb2: "bg-purple-500/10",
        textPrimary: "text-white",
        textSecondary: "text-white/60",
        textAccent: "text-white/40",
        bgText: "text-white/5",
        btnBg: "bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 shadow-[0_4px_30px_rgba(0,0,0,0.1)]",
        thumbBg: "bg-white/5 border-white/10",
        shadow: "bg-black/80",
        sizeBtnActive: "bg-white text-black shadow-[0_0_20px_rgba(255,255,255,0.3)]",
        sizeBtnInactive: "bg-white/5 text-white/80 hover:bg-white/15 border border-white/10"
      };
  }
};

const Petals = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      <style>{`
        @keyframes petal-fall {
          0% { transform: translateY(120vh) translateX(40vw) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-20vh) translateX(-40vw) rotate(720deg); opacity: 0; }
        }
        .petal {
          position: absolute;
          background: linear-gradient(135deg, #FFB7C5 0%, #FF69B4 100%);
          border-radius: 50% 0 50% 50%;
          box-shadow: 0 4px 10px rgba(255, 105, 180, 0.4);
          animation: petal-fall linear infinite;
        }
        .petal-bg-far { width: 8px; height: 8px; filter: blur(24px); z-index: 1; }
        .petal-bg-mid { width: 14px; height: 14px; filter: blur(18px); z-index: 2; }
        .petal-bg-near { width: 20px; height: 20px; filter: blur(12px); z-index: 3; }
        .petal-mid { width: 25px; height: 25px; filter: blur(10px); z-index: 20; }
        .petal-near { width: 32px; height: 32px; filter: blur(10px); z-index: 21; }
        .petal-extreme-foreground { width: 50px; height: 50px; filter: blur(20px); z-index: 50; }
        .petal-cinematic { width: 80px; height: 80px; filter: blur(32px); z-index: 60; }
      `}</style>
      
      {/* 7 layers of depth */}
      {/* Background Far */}
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={`bg-far-${i}`} className="petal petal-bg-far" style={{ left: `${Math.random() * 100}%`, animationDuration: `${25 + Math.random() * 15}s`, animationDelay: `-${Math.random() * 20}s` }} />
      ))}
      
      {/* Background Mid */}
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={`bg-mid-${i}`} className="petal petal-bg-mid" style={{ left: `${Math.random() * 100}%`, animationDuration: `${20 + Math.random() * 12}s`, animationDelay: `-${Math.random() * 15}s` }} />
      ))}
      
      {/* Background Near */}
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={`bg-near-${i}`} className="petal petal-bg-near" style={{ left: `${Math.random() * 100}%`, animationDuration: `${18 + Math.random() * 10}s`, animationDelay: `-${Math.random() * 12}s` }} />
      ))}
      
      {/* Midground */}
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={`mid-${i}`} className="petal petal-mid" style={{ left: `${Math.random() * 100}%`, animationDuration: `${15 + Math.random() * 8}s`, animationDelay: `-${Math.random() * 10}s` }} />
      ))}
      
      {/* Foreground */}
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={`near-${i}`} className="petal petal-near" style={{ left: `${Math.random() * 100}%`, animationDuration: `${12 + Math.random() * 6}s`, animationDelay: `-${Math.random() * 8}s` }} />
      ))}

      {/* Extreme Foreground */}
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={`extreme-${i}`} className="petal petal-extreme-foreground" style={{ left: `${Math.random() * 100}%`, animationDuration: `${9 + Math.random() * 5}s`, animationDelay: `-${Math.random() * 6}s` }} />
      ))}
      
      {/* Cinematic Foreground */}
      <div className="petal petal-cinematic" style={{ left: '20%', animationDuration: '8s', animationDelay: '-2s' }} />
      <div className="petal petal-cinematic" style={{ left: '80%', animationDuration: '12s', animationDelay: '-1s' }} />
    </div>
  );
};

const PromoBanner = () => {
  const [products, setProducts] = useState<Product[]>(DEFAULT_PRODUCTS);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (products.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % products.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [products.length]);

  if (products.length === 0) return null;

  const product = products[currentIndex];
  const design = getDesignConfig(currentIndex);

  return (
    <section 
      className="w-full min-h-[700px] md:min-h-[85vh] relative flex flex-col items-center justify-between overflow-hidden font-sans pt-12 pb-8 md:pb-12 bg-gray-900"
    >
      {/* Animated Crossfading Background Layer */}
      <AnimatePresence>
        <motion.div
          key={`bg-${currentIndex}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5, ease: "easeInOut" }}
          className="absolute inset-0 pointer-events-none z-0"
          style={{ background: design.bgStyle || `linear-gradient(to bottom right, ${design.gradientStart}, ${design.gradientEnd})` }}
        />
      </AnimatePresence>
      
      {/* Animated Background Orbs */}
      {!design.hideOrbs && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
          <motion.div 
            animate={{ x: [0, 100, 0], y: [0, -100, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className={`absolute top-0 left-0 w-[50vw] h-[50vw] rounded-full blur-[120px] transition-colors duration-1000 ${design.orb1}`} 
          />
          <motion.div 
            animate={{ x: [0, -100, 0], y: [0, 100, 0] }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className={`absolute bottom-0 right-0 w-[60vw] h-[60vw] rounded-full blur-[140px] transition-colors duration-1000 ${design.orb2}`} 
          />
        </div>
      )}

      {/* Glassmorphism Backdrop Filter */}
      <div className="absolute inset-0 backdrop-blur-[60px] bg-white/5 border-b border-white/10 pointer-events-none z-0" />

      {/* Huge Background Typography */}
      {!design.hideText && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0 overflow-hidden mix-blend-overlay">
          <AnimatePresence mode="wait">
            <motion.h1 
              key={`bgtext-${product.name}`}
              initial={{ opacity: 0, scale: 0.95, filter: "blur(20px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, scale: 1.05, filter: "blur(20px)" }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
              className={`text-[12rem] md:text-[22rem] lg:text-[28rem] font-black ${design.bgText} leading-none whitespace-nowrap tracking-tighter uppercase select-none transition-colors duration-1000`}
            >
              {product.name.split(' ')[0]}
            </motion.h1>
          </AnimatePresence>
        </div>
      )}

      {/* Cherry Blossom Petals */}
      {!design.hidePetals && <Petals />}

      {/* Center Product Area */}
      <div className="relative w-full max-w-[1400px] mx-auto flex items-center justify-center flex-1 z-10 px-6 mt-4">
        
        {/* Floating texts removed by request */}

        {/* The Product Image */}
        <div className="relative w-full max-w-[350px] md:max-w-[550px] lg:max-w-[650px] aspect-square flex items-center justify-center">
           <AnimatePresence>
             <motion.div 
               key={`large-${product.id || product.name}`}
               initial={{ opacity: 0, x: "100vw", y: "100vh", scale: 0.2, filter: "blur(30px)", clipPath: "circle(0% at 100% 100%)" }}
               animate={{ opacity: 1, x: 0, y: 0, scale: 1, filter: "blur(0px)", clipPath: "circle(150% at 50% 50%)", transition: { duration: 1.6, ease: "easeOut" } }}
               exit={{ opacity: 0, x: "-100vw", y: "-100vh", scale: 0, filter: "blur(30px)", clipPath: "circle(0% at 0% 0%)", transition: { duration: 1.6, ease: "easeIn" } }}
               className="absolute w-full h-full z-10 flex items-center justify-center pointer-events-none" 
             >
               <img 
                 src={product.image} 
                 alt={product.name}
                 className="w-full h-full object-contain scale-[1.2] drop-shadow-2xl" 
               />
             </motion.div>
           </AnimatePresence>
           
           {/* Realistic studio shadow under the image */}
           <div className="absolute -bottom-12 md:-bottom-20 left-1/2 -translate-x-1/2 w-4/5 md:w-2/3 h-8 flex items-center justify-center pointer-events-none z-0">
             {/* Wide soft shadow */}
             <div className={`absolute w-full h-full ${design.shadow} blur-2xl rounded-[100%] transition-colors duration-1000 opacity-70`} />
             {/* Tight dark core shadow */}
             <div className={`absolute w-1/2 h-3 md:h-4 ${design.shadow} blur-md md:blur-lg rounded-[100%] transition-colors duration-1000 opacity-100`} />
           </div>
        </div>

        {/* Floating texts removed by request */}
      </div>

      {/* Bottom Information Area */}
      <div className="w-full max-w-[1400px] mx-auto px-6 z-10 flex flex-col md:flex-row justify-between items-center md:items-end gap-6 mt-8">
        
        {/* Left: Product Info (Floats on Desktop) */}
        <div className="flex flex-col items-center md:items-start text-center md:text-left md:absolute md:left-10 lg:left-16 xl:left-24 md:top-[45%] md:-translate-y-1/2 md:w-[300px] lg:w-[380px] md:z-30">
          <AnimatePresence mode="wait">
            <motion.div
              key={`info-${product.id || product.name}`}
              initial={{ opacity: 0, x: -30, filter: "blur(15px)" }}
              animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, x: -30, filter: "blur(15px)" }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
              className="flex flex-col items-center md:items-start w-full"
            >
              <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${design.thumbBg} backdrop-blur-md mb-3`}>
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                <span className={`text-xs font-medium ${design.textPrimary} tracking-wide uppercase`}>New Arrival</span>
              </div>
              
              <h2 className={`text-3xl md:text-5xl lg:text-6xl font-black ${design.textPrimary} tracking-tight mb-4 leading-[1.1]`}>
                {product.name}
              </h2>
              
              <p className={`text-sm md:text-base ${design.textSecondary} max-w-sm md:pr-4 mb-6 md:mb-8`}>
                Experience ultimate comfort and minimalist style with our signature collection.
              </p>
              
              <button className={`group w-full md:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-sm uppercase tracking-wider transition-all duration-300 ${design.btnBg}`}>
                Add to Cart
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default PromoBanner;
